import { RefObject, useLayoutEffect, useState } from "react";

const MIN_HEIGHT = 360;
/** Breathing room when the table fills a screen it doesn't start on. */
const FULL_SCREEN_GAP = 16;

const isScrollable = (element: HTMLElement) => {
  const { overflowY } = window.getComputedStyle(element);
  return (
    overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay"
  );
};

/** Nearest ancestor that scrolls vertically, or null for the document. */
const getScrollParent = (element: HTMLElement | null): HTMLElement | null => {
  let node = element?.parentElement ?? null;
  while (node && node !== document.body) {
    if (isScrollable(node)) return node;
    node = node.parentElement;
  }
  return null;
};

const px = (value: string) => parseFloat(value) || 0;

const isStackedVertically = (element: HTMLElement) => {
  const { display, flexDirection } = window.getComputedStyle(element);
  if (display === "flex" || display === "inline-flex")
    return flexDirection.startsWith("column");
  return display !== "grid" && display !== "inline-grid";
};

const isInFlow = (element: Element): element is HTMLElement => {
  if (!(element instanceof HTMLElement)) return false;
  const { position, display } = window.getComputedStyle(element);
  return display !== "none" && position !== "absolute" && position !== "fixed";
};

/**
 * Space the page keeps below `element`: bottom margins, borders and padding
 * of every wrapper up to the scrolling container (e.g. the layout's bottom
 * padding), plus anything stacked after it. Independent of the table's own
 * height, so resizing the table doesn't feed back into the measurement.
 */
export const getSpaceBelow = (
  element: HTMLElement,
  scrollParent: HTMLElement | null,
) => {
  const stop = scrollParent ?? document.body;
  let space = px(window.getComputedStyle(element).marginBottom);
  let child: HTMLElement = element;
  let parent = element.parentElement;

  while (parent && child !== stop) {
    if (isStackedVertically(parent)) {
      let sibling = child.nextElementSibling;
      while (sibling) {
        if (isInFlow(sibling)) {
          const style = window.getComputedStyle(sibling);
          space +=
            sibling.offsetHeight + px(style.marginTop) + px(style.marginBottom);
        }
        sibling = sibling.nextElementSibling;
      }
    }
    const style = window.getComputedStyle(parent);
    space += px(style.paddingBottom) + px(style.borderBottomWidth);
    if (parent === stop) break;
    space += px(style.marginBottom);
    child = parent;
    parent = parent.parentElement;
  }
  return space;
};

type Measurements = {
  viewportHeight: number;
  /** Distance from the top of the scrolling page to the table body. */
  offsetTop: number;
  /** Table parts below the body: footer, borders. */
  chromeBelow: number;
  spaceBelow: number;
};

export const computeFittedHeight = ({
  viewportHeight,
  offsetTop,
  chromeBelow,
  spaceBelow,
}: Measurements) => {
  const fitted =
    offsetTop < viewportHeight
      ? // Starts on the first screen: end exactly where the page ends.
        viewportHeight - offsetTop - chromeBelow - spaceBelow
      : // Starts below the fold: fill one screen once scrolled to.
        viewportHeight - chromeBelow - FULL_SCREEN_GAP * 2;
  return Math.max(MIN_HEIGHT, Math.floor(fitted));
};

/**
 * Max height for a table's scroll area so the page ends at the bottom of the
 * screen, with the table's footer visible and no page scrollbar. Measured
 * from the table's position with the page scrolled to the top, and
 * re-measured when the window or page content resizes. Returns undefined
 * when disabled.
 */
export const useFitToViewport = (
  scrollRef: RefObject<HTMLElement>,
  enabled: boolean,
) => {
  const [maxHeight, setMaxHeight] = useState<number>();

  useLayoutEffect(() => {
    const scrollArea = scrollRef.current;
    const root = scrollArea?.parentElement;
    if (!enabled || !scrollArea || !root) {
      setMaxHeight(undefined);
      return undefined;
    }

    const scrollParent = getScrollParent(scrollArea);
    let frame = 0;

    const fit = () => {
      // clientHeight excludes a horizontal page scrollbar; innerHeight doesn't.
      const viewportHeight = scrollParent
        ? scrollParent.clientHeight
        : document.documentElement.clientHeight;
      const viewportTop = scrollParent
        ? scrollParent.getBoundingClientRect().top
        : 0;
      const scrolled = scrollParent ? scrollParent.scrollTop : window.scrollY;
      const areaRect = scrollArea.getBoundingClientRect();
      const rootRect = root.getBoundingClientRect();

      setMaxHeight(
        computeFittedHeight({
          viewportHeight,
          offsetTop: areaRect.top - viewportTop + scrolled,
          chromeBelow: Math.max(0, rootRect.bottom - areaRect.bottom),
          spaceBelow: getSpaceBelow(root, scrollParent),
        }),
      );
    };
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    };

    // The first measurement runs before paint, so the table never shows at
    // full height with a page scrollbar and then snaps to the fitted height.
    fit();
    window.addEventListener("resize", measure);
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    observer?.observe(scrollParent ?? document.body);
    // Content around the table (stats, charts, filters, footer) resizing.
    observer?.observe(root);
    const content = scrollParent?.firstElementChild ?? document.body;
    if (content instanceof HTMLElement) observer?.observe(content);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [enabled, scrollRef]);

  return maxHeight;
};
